import { Attachment } from "src/types";
import {
  TremendousProduct,
  TremendousProductSku,
  TremendousRewardProductMetadata,
} from "../types/tremendous.entity";

// Ordered from more specific to more general so partial matches stay stable.
const BRAND_MATCHERS: Array<{ pattern: RegExp; brandName: string }> = [
  { pattern: /\bbol\.com\b/i, brandName: "BOL_COM" },
  { pattern: /\buber eats\b/i, brandName: "UBER_EATS" },
  { pattern: /\bgoogle play\b/i, brandName: "GOOGLE_PLAY" },
  { pattern: /\bnintendo eshop\b/i, brandName: "NINTENDO_ESHOP" },
  { pattern: /\bmeta quest\b/i, brandName: "META_QUEST" },
  { pattern: /\bhotels\.com\b/i, brandName: "HOTELS_COM" },
  { pattern: /\bbest buy\b/i, brandName: "BEST_BUY" },
  { pattern: /\bdoordash\b/i, brandName: "DOORDASH" },
  { pattern: /\bcash app\b/i, brandName: "CASH_APP" },
  { pattern: /\bapp store\b|\bitunes\b/i, brandName: "APPLE" },
  { pattern: /\bamazon\b/i, brandName: "AMAZON" },
  { pattern: /\bpaypal\b/i, brandName: "PAYPAL" },
  { pattern: /\brazer\b/i, brandName: "RAZER" },
  { pattern: /\bsteam\b/i, brandName: "STEAM" },
  { pattern: /\badidas\b/i, brandName: "ADIDAS" },
  { pattern: /\bplaystation\b/i, brandName: "PLAYSTATION" },
  { pattern: /\buber\b/i, brandName: "UBER" },
  { pattern: /\bapple\b/i, brandName: "APPLE" },
  { pattern: /\bh&m\b/i, brandName: "H_M" },
  { pattern: /\bzalando\b/i, brandName: "ZALANDO" },
  { pattern: /\bikea\b/i, brandName: "IKEA" },
  { pattern: /\broblox\b/i, brandName: "ROBLOX" },
  { pattern: /\bnintendo\b/i, brandName: "NINTENDO" },
  { pattern: /\bgrab(?:gifts)?\b/i, brandName: "GRAB" },
  { pattern: /\bnike\b/i, brandName: "NIKE" },
  { pattern: /\btwitch\b/i, brandName: "TWITCH" },
  { pattern: /\bxbox\b/i, brandName: "XBOX" },
  { pattern: /\bairbnb\b/i, brandName: "AIRBNB" },
  { pattern: /\bdecathlon\b/i, brandName: "DECATHLON" },
  { pattern: /\bprimark\b/i, brandName: "PRIMARK" },
  { pattern: /\bsephora\b/i, brandName: "SEPHORA" },
  { pattern: /\bticketmaster\b/i, brandName: "TICKETMASTER" },
  { pattern: /\bstarbucks\b/i, brandName: "STARBUCKS" },
  { pattern: /\bcarrefour\b/i, brandName: "CARREFOUR" },
  { pattern: /\basos\b/i, brandName: "ASOS" },
  { pattern: /\blazada\b/i, brandName: "LAZADA" },
  { pattern: /\bshopee(?:pay)?\b/i, brandName: "SHOPEE" },
  { pattern: /\bsubway\b/i, brandName: "SUBWAY" },
  { pattern: /\bwalmart\b/i, brandName: "WALMART" },
  { pattern: /\bemag\b/i, brandName: "EMAG" },
  { pattern: /\bbolt\b/i, brandName: "BOLT" },
  { pattern: /\btarget\b/i, brandName: "TARGET" },
  { pattern: /\bwayfair\b/i, brandName: "WAYFAIR" },
  { pattern: /\bflipkart\b/i, brandName: "FLIPKART" },
  { pattern: /\btokopedia\b/i, brandName: "TOKOPEDIA" },
  { pattern: /\bnykaa\b/i, brandName: "NYKAA" },
  { pattern: /\bmyntra\b/i, brandName: "MYNTRA" },
  { pattern: /\bmango\b/i, brandName: "MANGO" },
  { pattern: /\bvenmo\b/i, brandName: "VENMO" },
  { pattern: /\bmastercard\b/i, brandName: "MASTERCARD" },
  { pattern: /\bvisa\b/i, brandName: "VISA" },
];

const BRAND_STOP_WORDS = new Set([
  "gift",
  "egift",
  "card",
  "cards",
  "voucher",
  "vouchers",
  "reward",
  "rewards",
  "prepaid",
]);

const toScreamingSnakeCase = (value: string): string =>
  value
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .toUpperCase();

export const extractRewardBrandName = (productName: string): string => {
  const normalizedName = productName.replace(/\s+/g, " ").trim();

  if (!normalizedName) return "OTHER";

  const knownBrand = BRAND_MATCHERS.find(({ pattern }) => pattern.test(normalizedName));

  if (knownBrand) {
    return knownBrand.brandName;
  }

  const brandTokens: string[] = [];

  for (const token of normalizedName.split(" ")) {
    const normalizedToken = token.toLowerCase().replace(/[^a-z0-9.]/g, "");
    if (!normalizedToken) continue;
    if (BRAND_STOP_WORDS.has(normalizedToken)) break;
    brandTokens.push(token);
  }

  return toScreamingSnakeCase(brandTokens.join(" ").trim() || normalizedName) || "OTHER";
};

export const getTremendousCurrencyOptions = (product?: TremendousProduct | null): string[] => {
  if (!product) return [];

  if (Array.isArray(product.currencyCodes) && product.currencyCodes.length) {
    return Array.from(new Set(product.currencyCodes.filter(Boolean))).sort();
  }

  return Array.from(
    new Set(
      product.skus.flatMap((sku) => sku.currencyCodes).filter(Boolean)
    )
  ).sort();
};

export const getTremendousSkuBounds = (
  product?: TremendousProduct | null,
  currencyCode?: string
): { min: number; max: number } | null => {
  if (!product || !product.skus.length) return null;

  const matchingSkus =
    currencyCode && Array.isArray(product.currencyCodes) && product.currencyCodes.includes(currencyCode)
      ? product.skus
      : currencyCode
        ? product.skus.filter((sku) => sku.currencyCodes.includes(currencyCode))
        : product.skus;

  if (!matchingSkus.length) return null;

  return {
    min: Math.min(...matchingSkus.map((sku) => sku.min)),
    max: Math.max(...matchingSkus.map((sku) => sku.max)),
  };
};

export const buildTremendousAttachment = (product?: TremendousProduct | null): Attachment | null => {
  if (!product?.imageUrl) return null;

  const safeFileName =
    product.name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "tremendous-product";

  return {
    fileName: `${safeFileName}.png`,
    fileSizeInKB: "0",
    fileType: "IMAGE",
    fileUrl: product.imageUrl,
  };
};

export const buildTremendousProductMetadata = (
  product: TremendousProduct,
  currencyCode: string,
  denomination: number
): TremendousRewardProductMetadata | null => {
  const bounds = getTremendousSkuBounds(product, currencyCode);
  if (!bounds) return null;

  return {
    productId: product.id,
    productNameSnapshot: product.name,
    brandName: extractRewardBrandName(product.name),
    category: product.category,
    subcategory: product.subcategory,
    currencyCode,
    denomination,
    skuMin: bounds.min,
    skuMax: bounds.max,
    countries: product.countries,
    imageUrl: product.imageUrl,
    descriptionSnapshot: product.description,
    disclosure: product.disclosure,
  };
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isSkuArray = (value: unknown): value is TremendousProductSku[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as TremendousProductSku).min === "number" &&
      typeof (item as TremendousProductSku).max === "number" &&
      isStringArray((item as TremendousProductSku).currencyCodes)
  );

export const isTremendousProduct = (value: unknown): value is TremendousProduct => {
  if (!value || typeof value !== "object") return false;

  const product = value as TremendousProduct;
  return (
    typeof product.id === "string" &&
    typeof product.name === "string" &&
    typeof product.category === "string" &&
    isStringArray(product.countries) &&
    (!product.currencyCodes || isStringArray(product.currencyCodes)) &&
    isSkuArray(product.skus)
  );
};

export const isTremendousRewardProductMetadata = (
  value: Record<string, unknown> | null | undefined
): value is TremendousRewardProductMetadata => {
  if (!value) return false;

  return (
    typeof value.productId === "string" &&
    typeof value.productNameSnapshot === "string" &&
    typeof value.category === "string" &&
    typeof value.currencyCode === "string" &&
    typeof value.denomination === "number" &&
    typeof value.skuMin === "number" &&
    typeof value.skuMax === "number" &&
    isStringArray(value.countries)
  );
};
